/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Portal Template Registry & Layout Resolver (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { AdminLayout, type AdminLayoutProps } from '../components/layout/AdminLayout';
import { CyberpunkLayout } from './cyberpunk-noc/CyberpunkLayout';
import { CompactLayout } from './compact/CompactLayout';

export type AdminLayoutComponent = React.ComponentType<AdminLayoutProps>;

export interface AdminTemplateRegistration {
  id: string;
  name: string;
  description: string;
  component: AdminLayoutComponent;
  tags: string[];
}

export const ADMIN_TEMPLATES_REGISTRY: Record<string, AdminTemplateRegistration> = {
  default: {
    id: 'default',
    name: 'Default Mission Control Shell',
    description: 'Bố cục 2 cột cố định chuẩn mực cho Super Admin với 16 module quản trị và Recharts BI.',
    component: AdminLayout,
    tags: ['mission-control', 'two-column', 'bi-charts'],
  },
  'cyberpunk-noc': {
    id: 'cyberpunk-noc',
    name: 'Cyberpunk NOC Console',
    description: 'Bố cục trung tâm điều hành giám sát mạng tối màu Obsidian, viền neon phát sáng, footer Edge status.',
    component: CyberpunkLayout,
    tags: ['cyberpunk', 'noc', 'dark-console'],
  },
  // Alias support
  cyberpunk: {
    id: 'cyberpunk',
    name: 'Cyberpunk NOC Console (Alias)',
    description: 'Bố cục trung tâm điều hành giám sát mạng tối màu Obsidian, viền neon phát sáng, footer Edge status.',
    component: CyberpunkLayout,
    tags: ['cyberpunk', 'noc', 'dark-console'],
  },
  compact: {
    id: 'compact',
    name: 'Compact Admin Console',
    description: 'Sidebar siêu gọn 56px chỉ hiển thị biểu tượng với tooltip, tối đa diện tích cho các bảng dữ liệu lớn.',
    component: CompactLayout,
    tags: ['compact', 'laptop', 'data-dense'],
  },
  'compact-console': {
    id: 'compact-console',
    name: 'Compact Admin Console (Alias)',
    description: 'Sidebar siêu gọn 56px chỉ hiển thị biểu tượng với tooltip, tối đa diện tích cho các bảng dữ liệu lớn.',
    component: CompactLayout,
    tags: ['compact', 'laptop', 'data-dense'],
  },
};

/**
 * Returns the layout component corresponding to the template ID.
 * Defaults to AdminLayout (default) if not found.
 */
export function getAdminTemplate(templateId?: string): AdminLayoutComponent {
  if (!templateId) return AdminLayout;
  const normalized = templateId.trim().toLowerCase();
  const reg = ADMIN_TEMPLATES_REGISTRY[normalized];
  return reg ? reg.component : AdminLayout;
}

/**
 * Checks whether the template ID is registered in the registry.
 */
export function isValidAdminTemplate(templateId?: string): boolean {
  if (!templateId) return false;
  return Boolean(ADMIN_TEMPLATES_REGISTRY[templateId.trim().toLowerCase()]);
}

/**
 * Lists all registered admin templates.
 */
export function listAdminTemplates(): AdminTemplateRegistration[] {
  return Object.values(ADMIN_TEMPLATES_REGISTRY);
}

